import EnvConfig from '../Config/env.config';
import { getURLS } from '../Helpers/String.helper';

export const externalUrls = {
  PRO_URL: EnvConfig.PRO_URL,
  BOOKING_TOOL_URL: EnvConfig.BOOKING_TOOL_URL,
};

export const userServiceUrls = {
  ACCESS_TOKEN: '/connect/token',
  PASSWORD_RESET: '/password_resets',
  CHANGE_PASSWORD: '/users/password',
  NEW_PASSWORD: '/password_resets/noid',
  REGISTER: '/register',
  MY_ACCOUNT: '/api/users/myaccount',
  USER: '/api/users',
  USER_FROM_UUID: '/api/users/:uuid',
  ROLES: '/api/roles',
  VERIFY_EMAIL: '/users/verifyEmail',
  VERIFY_ACCOUNT_TOKEN: '/verify-email-token',
  RESEND_EMAIL_VERIFICATION: '/resend-verification-token',
  PUBLIC_API_ROLES: '/public-api/roles/',
  PUBLIC_API_CLIENT: '/api/clients/:clientId',
  ORG_ROLES: '/api/roles/organisation',
  UPDATE_INT_ROLES: '/api/roles',
  DELETE_INTEGRATION: '/public-api/roles',
  PROD_INTEGRATIONS: '/api/clients/all?prodOnly=:prodOnly',
  PROD_ACTIVE_INTEGRATIONS: '/api/clients/all?prodOnly=:prodOnly&providerId=:providerId',
};

type UserServiceURLS = typeof userServiceUrls;

export const eventServiceUrls = {
  BULK: '/bulk',
  EVENT_LIST: '/events',
  EVENT_DETAIL: '/events/:id',
  EVENT_GROUPS: '/eventgroups',
  FREE_TRAININGS: '/freetrainings',
  SINGLE_EVENTGROUP: '/eventgroups/:id',
  SINGLE_FREE_TRAININGS: '/freetrainings/:id',
  EVENT_CATEGORY_TYPES: '/categorytypes',
  SPORT_CATEGORY_TYPES: '/eventgroupcategories',
  GET_TEXTS: '/texts/:key',
  TEXTS: '/texts/:key/:language',
  EVENT_SERIES: '/eventseries',
  EVENT_SERIES_DETAIL: '/eventseries/:id',
  EVENT_SERIES_GET: '/eventseries?eventGroupId=:eventGroupId&providerId=:providerId',
  GET_INTEGRATION_DATES: '/publicapisettings/:providerId?clientId=:clientId',
  EDIT_INTEGRATION_DATES: '/publicapisettings/:providerId',
  LOCATION_CANCELLATION_WINDOW: '/location-cancellation-window/:locationId',
};
type EventServiceURLS = typeof eventServiceUrls;

export const providerServiceUrls = {
  AVAILABLE_PROVIDERS: '/providers/available',
  PROVIDER_LIST_SEARCH: '/providers',
  PROVIDER_DETAIL: '/providers/:id',
  PROVIDER_DETAIL_UPDATE: '/service_providers/:id',
  ACTIVE_LOCATIONS: '/locations/:providerUUID/active',
  LOCATION_LIST: '/locations/:providerUUID',
  LOCATION: '/locations/:locationId',
  LOCATION_CREATE: '/locations/:domain',
  TRAINER_LIST: '/trainers/:id',
  CATEGORY_LIST: '/service_provider_categories',
  VERIFY_STUDIO_NAME: '/providers/verifyStudioName',
  CREATE_PROVIDER: '/service_providers',
  CHECK_LIST: '/providers/:domain/checklist',
  STAFF_LIST: '/staff',
  STAFF_DETAIL: '/staff/:staffId?providerId=:providerId',
  STAFF_UPDATE: '/staff/:staffId',
  ORGANISATION: '/organisations/:organisationId',
  ORGANISATION_SETTINGS: '/organisation-setting/:organisationId',
  ORGANISATION_STUDIO_LIST: '/organisations/:organisationId/providers',
  ORGANISATION_STAFF_LIST: '/organisations/:organisationId/staff',
  ORGANISATION_STAFF: '/organisations/:organisationId/staff',
  ORGANISATION_STAFF_DETAIL: '/organisations/:organisationId/staff/:staffId',
  ORGANISATION_CREATE: '/organisations',
  STUDIO_CATEGORIES: '/service_provider_categories',
  MERCHANT_LIST: '/merchants/venue/:organisationId',
  MERCHANT_UPDATE: '/merchants',
  MERCHANT_GET_RATES: '/merchants/:merchantId/rates/1',
  MERCHANT_GET_DEFAULT_RATE: '/merchants/:merchantId/rate/1/default',
  MERCHANT_SET_DEFAULT_RATE: '/merchants/rate/default',
  MERCHANT_ASSIGN_LOCATION: '/merchants/location/assign',
  MERCHANT_VALIDATE_IBAN: '/merchants/iban/:iban',
  GET_CITY_COUNTRY_LIST: '/tool/cache/countries',
  GET_INVOICES_BY_MERCHANT: '/payouts/providers/:providerId/merchants/:merchantId/payout-invoices',
  GET_INVOICE_DOWNLOAD_URL: '/payouts/providers/:providerId/payout-invoices/:invoiceId/url',
  GET_PLACES_AUTO_COMPLETE: '/places/autocomplete?input=:input&languageCode=:languageCode',
  GET_PLACES_DETAILS: '/places/details?Place=places/:place&LanguageCode=:languageCode',
  GET_LOCATION_FIRST_FREE_CHECKIN: '/location-settings/:locationId',
  UPDATE_LOCATION_FIRST_FREE_CHECKIN: '/location-settings',
};

type ProviderServiceURLS = typeof providerServiceUrls;

const coreServiceUrls = {
  CUSTOMER_LIST: '/providers/:domain/customers/simple',
  PROVIDER_IMAGES: '/providers/:domain/images',
  SINGLE_CUSTOMER: '/customers/:id',
  CREATE_CUSTOMER: '/customers',
  UPDATE_CUSTOMER: '/providers/:providerId/customers/:id',
  LOCATION_LIST: '/providers/:providerId/locations',
  BOOKING_LIST: '/providers/:providerId/bookings/simple',
  BOOKING_PRESENCE_UPDATE: '/bookingUpdates/presence',
  BOOKING_PRESENCE_UPDATE_MULTIPLE: '/bookingUpdates/presence/multiple',
  BOOKING_UPDATE: '/bookingUpdates/status/multiple',
  BOOKING_UPDATE_SINGLE: '/bookingUpdates/status',
  BOOKING_SUMMARY: '/event-booking-summaries',
  CREATE_SINGLE_BOOKING: '/bookings',
  COUNT_BOOKINGS: '/bookings/count',
  UPLOAD_IMAGE: '/providers/:domain/images',
  INVITE_CUSTOMER: '/providers/:providerId/customers/:id/invite',
  DISCONNECT_CUSTOMER: '/customers/:id/disconnect',
  EVENT_PRODUCTS: '/event/:eventId/products',
  CHECKOUT: '/providers/:domain/checkout',
  CUSTOMER_ACTIVE_ORDERLINES: '/provider/:providerId/customers/:customerId/orderlines/active',
  CUSTOMER_ARCHIVED_ORDERLINES: '/provider/:providerId/customers/:customerId/orderlines/archived',
  CUSTOMER_EVENT_ORDERLINES: '/event/:eventId/customers/:customerId/orderlines',
  GET_PAYMENTMETHODS: '/providers/:domain/payment-methods',
  CREATE_PAYMENT_FOR_ORDERLINE: '/orderlines/:orderlineId/payments',
  CREATE_ORDERLINE: '/customers/:customerid/orderlines',
  EXPORT_CUSTOMERS: '/providers/:domain/download-jobs/customers',
  DOWNLOAD_JOBS: '/providers/:domain/download-jobs',
  FULL_BOOKING_LIST: '/providers/:domain/bookings',
  COUNTRIES: '/countries',
  CUSTOMER_TAGS: '/customer-tags',
  FEATURE_KEYS: '/providers/:id/feature-keys',
  // ORDERLINES
  CUSTOMER_ORDERLINES: '/provider/:domain/customers/:customerId/orderlines/active',
  // PRODUCTS
  PRODUCT_LIST: '/providers/:domain/products',
  PRODUCT_UPDATE: '/products/:productId',
  PRODUCT_CREDITPASS_CREATE: '/products/creditpass',
  PRODUCT_CREDITPASS_UPDATE: '/products/:id/creditpass',
  PRODUCT_CONTRACT_CREATE: '/products/contract',
  PRODUCT_CONTRACT_UPDATE: '/products/:id/contract',
  PRODUCT_EVENT_TICKET_CREATE: '/products/course',
  PRODUCT_EVENT_TICKET_UPDATE: '/products/:id/course',
  // PAYMENT
  CURRENCIES: '/currencies',
  TAXGROUPS: '/providers/:domain/taxgroups',
  BOOKING_GROUPS: '/providers/:domain/booking-groups',
  // ACCOUNTING
  PROVIDER_ACCOUNT: '/accounting/:domain',
  // INTEGRATION
  SYNCED_EVENT_GROUPS: '/synced-eventgroups',
  SYNCED_EVENT_GROUPS_BY_ID: '/synced-eventgroups/:id',
  EXTERNAL_PARTIES: '/external-parties?domain=:domain',
  EXTERNAL_PARTIES_LOCATION: '/external-parties/integrations/locations/:id',
  //
  CORE_PROVIDER: '/providers/:domain',
  // FORCE ERRORS
  VALIDATION_ERROR: '/errors/validation-error',
  VALIDATION_ERROR_PAGE_SIZE: '/errors/validation-error?pageSize=5000',
  INVALID_ARGUMENT_ERROR: '/errors/invalid-argument',
  INVALID_OPERATION_ERROR: '/errors/invalid-operation',
  // ATTENDANCE
  GET_ATTENDANCE_LIST: '/providers/:providerId/checkins_summary',
  STUDIO_COUNTRIES: '/countries',
  // MANUAL BOOKINGS
  PENDING_BOOKINGS: '/pending-bookings',
  PENDING_BOOKINGS_SETTINGS: '/partnerbookingsetting/:domain',
  //Exports
  GET_EVERSPORTS_AUTH_LINK: '/providers/:domain/eversports-auth-requestbody',
  GET_EXPORT_STATUS: '/providers/:uuid/export',
  START_EXPORT: '/providers/:domain/export',
  //Exports Schedule
  EXPORT_SCHEDULE: '/providers/:domain/schedule-export',
  EXPORT_SCHEDULE_STATUS: '/providers/:domain/schedule-export/:exportUuid',
};

type CoreServiceURLS = typeof coreServiceUrls;

const importServiceUrls = {
  IMPORT_FILE: '/imports/:id/file',
  IMPORT: '/imports',
  IMPORT_ID: '/imports/:id',
  IMPORT_CUSTOMERS_DATA: '/CustomersData',
  IMPORT_DATA_SCHEMA: '/DataSchemas',
  IMPORT_TEMPLATE: '/Templates',
  IMPORT_PROCESS_RUN: '/ProcessRuns',
};

type IImportServiceUrls = typeof importServiceUrls;

// These are premade transformations for Cloudinary
// For more information see: https://cloudinary.com/documentation/transformation_flags
const cloudinaryTransforms = {
  AVATAR_SMALL: '/f_auto,h_40/',
  AVATAR_LARGE: '/f_auto,h_65/',
  CUSTOMER_PROFILE: '/f_auto,h_85/',
  CUSTOM: '/',
};

type CloudinaryURLS = typeof cloudinaryTransforms;

const zoomUrls = {
  INIT_ZOOM: '/zoom-integrations',
  GET_ZOOM_INTEGRATION_API: '/zoom-integrations/query',
  GET_ZOOM_SESSION_SETUP_API: '/zoom-session-setups/query',
  ZOOM_SESSION_SETUP_BASE_API: '/zoom-session-setups',
  REMOVE_ZOOM_INTEGRATION: '/zoom-integrations/',
  GET_ZOOM_INTEGRATION_ISSUES_API: '/zoom-integration-issues/query',
};

type IZoomURLS = typeof zoomUrls;

const NotificationServiceURLS = {
  GET_NOTIFICATION_SETTINGS: '/notification-settings/:id',
  GET_NOTIFICATION_SETTINGS_BY_USER: '/notification-settings/:id?userUUID=:userUUID',
};

type INotificationServiceURLS = typeof NotificationServiceURLS;

export const adapterSvcUrls = {
  GET_INTEGRATION_CHANNELS: '/providers/:providerId/channels',
  GET_INTEGRATION_CHANNEL_BY_ID: '/channels/:channelId',
  POST_INTEGRATION_CHANNEL: '/locations/:location/channels',
  GET_LOCATIONS_INTEGRATION_CHANNELS: '/locations/:location/channels',
  UPDATE_INTEGRATION_CHANNEL: '/channels/:channelId',
  FETCH_CHANNEL_CATEGORIES: '/channels/:channelId/fetch',
  FETCH_CHANNEL_CLASSES: '/channels/:channelId/fetchAsync',
  GET_CHANNEL_CATEGORIES: '/channels/:channelId/categories',
  ACTIVATE_CHANNEL: '/channels/:channelId/activate',
  DEACTIVATE_CHANNEL: '/channels/:channelId/deactivate',
  CLEANUP_CHANNEL: '/channels/:channelId/cleanup',
};

type AdapterSvcURLS = typeof adapterSvcUrls;

export const videoIntegrationServiceUrls = {
  VOD_INTEGRATIONS: '/integrations',
  VOD_GET_INTEGRATION: '/integrations/:integrationId',
  VOD_PATCH_INTEGRATION: '/integrations/:integrationId',
  VOD_VIDEOS: '/videos',
  VOD_PATCH_VIDEO: '/videos/:videoId',
  VOD_DELETE_VIDEO: '/videos/:videoId',
  VOD_GET_VIDEO: '/videos/:videoId',
  VOD_UPLOADS: '/uploads',
  VOD_UPDATE_THUMBNAIL: '/videos/:videoId/thumbnail',
  VOD_VIDEOS_URL: '/signed-urls',
};

type VideoIntegrationServiceURLS = typeof videoIntegrationServiceUrls;

export const USER_SERVICE_URLS = getURLS<UserServiceURLS>(
  EnvConfig.USER_SERVICE_URL,
  userServiceUrls,
);

export const EVENT_SERVICE_URLS = getURLS<EventServiceURLS>(
  EnvConfig.EVENT_SERVICE_URL,
  eventServiceUrls,
);

export const CORE_SERVICE_URLS = getURLS<CoreServiceURLS>(
  EnvConfig.CORE_SERVICE_URL,
  coreServiceUrls,
);

export const PROVIDER_SERVICE_URLS = getURLS<ProviderServiceURLS>(
  EnvConfig.PROVIDER_SERVICE_URL,
  providerServiceUrls,
);

export const CLOUDINARY_URLS = getURLS<CloudinaryURLS>(
  EnvConfig.CLOUDINARY_URL,
  cloudinaryTransforms,
);

export const IMPORT_URLS = getURLS<IImportServiceUrls>(
  EnvConfig.IMPORT_SERVICE_URL,
  importServiceUrls,
);

export const ZOOM_URLS = getURLS<IZoomURLS>(EnvConfig.ZOOM_SERVICE_URL, zoomUrls);

export const NOTIFICATION_SERVICE_URLS = getURLS<INotificationServiceURLS>(
  EnvConfig.NOTIFICATION_SERVICE_URL,
  NotificationServiceURLS,
);

export const ADAPTER_SVC_PARTNER_PORTAL_URLS = getURLS<AdapterSvcURLS>(
  EnvConfig.ADAPTER_SVC_PARTNER_PORTAL,
  adapterSvcUrls,
);

export const EXTERNAL_API_URLS = {
  VALIDATE_IBAN: (iban: string) =>
    `https://openiban.com/validate/${iban}?getBIC=true&validateBankCode=true`,
  VALIDATE_VATID_EU: 'https://validatevat.onrender.com/validateVAT',
};

export const VIDEO_INTEGRATION_SERVICE_URLS = getURLS<VideoIntegrationServiceURLS>(
  EnvConfig.VIDEO_INTEGRATION_SERVICE_URL,
  videoIntegrationServiceUrls,
);
